/**
 * Document-kind metadata used across the rail and dialogs.
 * Backend canonical names live in ``DocumentKind`` (codegen).
 */

import type { DocumentKind, AppealType } from "@/api/types";

export interface KindMeta {
  kind: DocumentKind;
  label: string;
  /** Group heading the rail shows this kind under. */
  group: "EDI" | "Letterhead" | "Supporting";
  /** Default short filename used for synthetic generations. */
  defaultFilename: string;
  /** One-line hint shown under the label in the Add-document menu. */
  description: string;
  /** Whether the user can upload this kind from disk. */
  uploadable: boolean;
  /** Whether the synth pipeline can produce this kind. */
  synthesizable: boolean;
}

export const KINDS: KindMeta[] = [
  // EDI
  {
    kind: "edi_837",
    label: "837 — Claim (EDI)",
    group: "EDI",
    defaultFilename: "837.edi",
    description: "Electronic claim the provider sent to the payer.",
    uploadable: true,
    synthesizable: true,
  },
  {
    kind: "edi_835",
    label: "835 — Remittance (EDI)",
    group: "EDI",
    defaultFilename: "835.edi",
    description: "Payer's electronic payment / denial response with CARC codes.",
    uploadable: true,
    synthesizable: true,
  },
  // PDF — real upload + synth
  {
    kind: "ub04",
    label: "UB-04 / CMS-1450",
    group: "Supporting",
    defaultFilename: "UB04.pdf",
    description: "Institutional claim form — paper equivalent of an 837I.",
    uploadable: true,
    synthesizable: true,
  },
  {
    kind: "eob",
    label: "EOB — Explanation of Benefits",
    group: "Supporting",
    defaultFilename: "EOB.pdf",
    description: "Human-readable payer denial statement.",
    uploadable: true,
    synthesizable: true,
  },
  {
    kind: "medical_record",
    label: "Medical Record",
    group: "Supporting",
    defaultFilename: "MR.pdf",
    description: "Clinical notes (H&P, progress notes, labs) that justify the service.",
    uploadable: true,
    synthesizable: true,
  },
  {
    kind: "prior_auth",
    label: "Prior Authorization",
    group: "Supporting",
    defaultFilename: "PriorAuth.pdf",
    description: "Payer's pre-service approval or denial letter.",
    uploadable: true,
    synthesizable: true,
  },
  {
    kind: "operative_report",
    label: "Operative Report",
    group: "Supporting",
    defaultFilename: "OpReport.pdf",
    description: "Surgeon's narrative of the procedure performed in the OR.",
    uploadable: true,
    synthesizable: true,
  },
  // PDF — synth-only (new in C3)
  {
    kind: "letter_of_medical_necessity",
    label: "Letter of Medical Necessity",
    group: "Supporting",
    defaultFilename: "LOMN.pdf",
    description: "Attending physician's signed letter justifying the requested service.",
    uploadable: false,
    synthesizable: true,
  },
  {
    kind: "interqual_worksheet",
    label: "InterQual / MCG Worksheet",
    group: "Supporting",
    defaultFilename: "InterQual.pdf",
    description: "SI/IS criteria worksheet supporting inpatient or higher level-of-care.",
    uploadable: false,
    synthesizable: true,
  },
  {
    kind: "discharge_summary",
    label: "Discharge Summary",
    group: "Supporting",
    defaultFilename: "DischargeSummary.pdf",
    description: "Hospital course + disposition for the inpatient stay.",
    uploadable: false,
    synthesizable: true,
  },
  {
    kind: "authorization_log",
    label: "Authorization Call / Fax Log",
    group: "Supporting",
    defaultFilename: "AuthLog.pdf",
    description: "Timestamped call / fax record of pre-cert attempts.",
    uploadable: false,
    synthesizable: true,
  },
  {
    kind: "payer_policy",
    label: "Payer Policy Excerpt",
    group: "Supporting",
    defaultFilename: "PayerPolicy.pdf",
    description: "Excerpt from the payer's published medical policy / NCCI edit.",
    uploadable: false,
    synthesizable: true,
  },
  {
    kind: "timely_filing_proof",
    label: "Timely Filing Proof",
    group: "Supporting",
    defaultFilename: "TimelyFilingProof.pdf",
    description: "Clearinghouse acceptance report showing on-time submission.",
    uploadable: false,
    synthesizable: true,
  },
  // Provider letterhead (image) — stamped on the appeal letter's first page
  {
    kind: "letterhead",
    label: "Letterhead",
    group: "Letterhead",
    defaultFilename: "Letterhead.png",
    description: "Provider header image (PNG/JPEG) printed on the appeal letter's first page.",
    uploadable: true,
    synthesizable: true,
  },
  // Catch-all
  {
    kind: "other",
    label: "Other supporting doc",
    group: "Supporting",
    defaultFilename: "Other.pdf",
    description: "Anything else useful: prior-payment history, literature, custom payer form.",
    uploadable: true,
    synthesizable: false,
  },
];

export const ALLOWED_FIRST_KINDS: ReadonlyArray<DocumentKind> = ["edi_837", "ub04"];

/** Kinds the synthetic generator knows how to produce. Mirrors
 *  ``services/document.py::SYNTHESIZABLE_KINDS``. */
export const SYNTHESIZABLE_KINDS: ReadonlyArray<DocumentKind> = KINDS.filter(
  (k) => k.synthesizable,
).map((k) => k.kind);

/** Kinds the user can upload from disk. Mirrors
 *  ``services/document.py::UPLOADABLE_KINDS``. */
export const UPLOADABLE_KINDS: ReadonlyArray<DocumentKind> = KINDS.filter(
  (k) => k.uploadable,
).map((k) => k.kind);

/** Only these doc kinds actually encode the denial reason, so they
 *  need an appeal type before synthesis. 837 / UB04 / MR are appeal-
 *  type-independent — they're the claim submission or the supporting
 *  evidence, not the denial response. */
export const APPEAL_TYPE_REQUIRED_KINDS: ReadonlyArray<DocumentKind> = ["edi_835", "eob"];

export function kindMeta(kind: DocumentKind): KindMeta {
  return KINDS.find((k) => k.kind === kind) ?? KINDS[KINDS.length - 1];
}

export function isEdi(kind: DocumentKind): boolean {
  return kind === "edi_835" || kind === "edi_837";
}

/** Documents that establish *what was billed* — the claim submission. */
export function isClaimDoc(kind: DocumentKind): boolean {
  return kind === "edi_837" || kind === "ub04";
}

/** Documents that establish *why it was denied* — the payer response. */
export function isDenialDoc(kind: DocumentKind): boolean {
  return kind === "edi_835" || kind === "eob";
}

// ---- Appeal types --------------------------------------------------
export interface AppealTypeMeta {
  slug: AppealType;
  title: string;
  carc: string;
  description: string;
}

// Ordered strongest → weakest for our generator. Technical / reconsideration
// denials (no-auth, timely filing, coding, eligibility, DRG) are where we add
// the most value, so they lead; clinical denials (non-covered, medical
// necessity) — where the outcome hinges on the underlying record more than the
// letter — come last.
export const APPEAL_TYPES: AppealTypeMeta[] = [
  {
    slug: "no-authorization",
    title: "No Authorization",
    carc: "197",
    description: "Precert / auth was absent. Auth Denial Letter and Pt Folder are the centerpiece.",
  },
  {
    slug: "coding-bundling",
    title: "Coding / Bundling",
    carc: "97",
    description:
      "Service lines bundled into a paid line. UB04's detailed CPT/HCPCS list is the key evidence.",
  },
  {
    slug: "drg-downgrade",
    title: "DRG Downgrade",
    carc: "N/A",
    description:
      "Payer accepted the stay but re-grouped it to a lower-weight DRG and short-paid the claim — argue the record supports the billed DRG.",
  },
  {
    slug: "non-covered",
    title: "Non-Covered / Benefit Exclusion",
    carc: "96",
    description:
      "Denied as non-covered / cosmetic. Argue the service is functionally indicated and a covered benefit — payer policy + MR are key.",
  },
  {
    slug: "medical-necessity",
    title: "Medical Necessity",
    carc: "50",
    description:
      "Services denied as not medically necessary. The MR is the central piece of supporting evidence.",
  },
];

export function appealTypeMeta(slug: AppealType): AppealTypeMeta {
  return APPEAL_TYPES.find((s) => s.slug === slug) ?? APPEAL_TYPES[0];
}

/** Render a (possibly mixed) appeal-type selection as a human label,
 * e.g. ``"Medical Necessity + No Authorization"``. The first appeal type
 * is the primary. */
export function appealTypesLabel(
  appealTypes: AppealType[] | null | undefined,
): string | null {
  if (!appealTypes || appealTypes.length === 0) return null;
  return appealTypes.map((s) => appealTypeMeta(s).title).join(" + ");
}
